// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
  View as RNView,
  type LayoutChangeEvent,
} from 'react-native';
import { useLocalSearchParams } from 'expo-router';

import { Text, View } from '@/components/Themed';
import PlayerAvatar from '@/components/PlayerAvatar';
import { PositionByHoleGraph, type PositionByHoleSeriesEntry } from '@/components/PositionByHoleGraph';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getPlayerAvatarImageSource } from '@/services/playerAvatarImageSource';
import { appendClaimQuery, buildAccessHeaders, type AccessCredentials } from '@/services/claimHttp';
import { getSharedBffWebSocket, type RoundStateEvent } from '@/services/bffWebSocket';
import { formatBoardCode, normalizeBoardCode } from '@/services/boardCode';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { buildBoardStandings, type BoardStandings } from '@/features/round-board/standings';
import BoardCurrentHole from '@/features/round-board/BoardCurrentHole';
import appColors, { alpha } from '@/theme/colors';

/**
 * The live round board: a leaderboard for a screen on a wall, updating itself as scores land.
 *
 * Built for a display nobody touches. There is no scrolling anywhere and nothing is hidden below a
 * fold — a wall screen has no one to reach for its scrollbar, so anything that needs scrolling to
 * be seen is simply not shown. The board measures the space it was given and sizes itself into it:
 * the row height and every font come from the height available, and when there are more players
 * than fit at a legible size it pages through them on a timer rather than shrinking to nothing.
 *
 * Three zones: a one-line headline, the standings across the full width, and a bottom strip
 * carrying the hole being played beside a graph of how the places have moved. There is deliberately
 * no eighteen-column grid — two-digit numbers in eighteen columns are unreadable across a room, and
 * the hole in front of the group is what a spectator actually wants.
 *
 * Read-only by construction. It is reached with a board (spectator) code rather than an invite
 * token, and that code buys reads and nothing else — the BFF refuses to let it claim a player, so
 * a code left on a screen all afternoon cannot be turned into a scorer.
 *
 * Everything shown is decided by a service: score points and merits by the scoring engine, gross
 * and net and par by tour-service. The page groups and orders, and calculates nothing.
 */

/** A quiet re-fetch after a score lands, coalescing the burst a whole group entering scores makes. */
const REFRESH_DEBOUNCE_MS = 600;

/**
 * Belt-and-braces poll. The WebSocket is the real update path; this catches a socket that dropped
 * without anybody noticing, which on an unattended screen nobody would.
 */
const POLL_INTERVAL_MS = 60_000;

/** Below this a row stops being readable across a room, and the board pages instead of shrinking. */
const MIN_ROW_HEIGHT = 46;

/**
 * A ceiling only so a two-ball does not become two bands the height of a door. Set generously: on a
 * wall the failure that matters is a half-empty screen with a dead band under the last player, not
 * rows that are too tall.
 */
const MAX_ROW_HEIGHT = 150;

/** Long enough to read a full page from across the room before it turns. */
const PAGE_INTERVAL_MS = 12_000;

/**
 * The smallest current-hole row worth putting on a wall. The panel shows the same page of players
 * as the standings, so a page that is too long for the panel is too long full stop — this is what
 * caps the page size when the panel is on screen.
 */
const MIN_HOLE_PANEL_ROW_HEIGHT = 24;

type RoundMeta = {
  name?: string;
  Name?: string;
  courseName?: string;
  CourseName?: string;
  status?: string;
  Status?: string;
  createdAt?: string;
  CreatedAt?: string;
};

type MeritsByHole = { holes: number[]; series: PositionByHoleSeriesEntry[] };

const readText = (source: RoundMeta | null, ...keys: (keyof RoundMeta)[]): string => {
  if (!source) {
    return '';
  }
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

/** Merits are fractional for a shared place, so never print 3.5 as 4 — or as 3.50. */
const formatNumber = (value: number | null): string =>
  value === null ? '–' : Number.isInteger(value) ? String(value) : value.toFixed(1);

export default function RoundBoardScreen() {
  const { t, locale } = useI18n();
  const { user } = useUser();
  const { height } = useWindowDimensions();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const {
    roundId: roundIdParam,
    boardToken: boardTokenParam,
  } = useLocalSearchParams<{ roundId?: string; boardToken?: string }>();

  const roundId = typeof roundIdParam === 'string' ? roundIdParam : '';
  const boardToken = useMemo(
    () => (typeof boardTokenParam === 'string' ? normalizeBoardCode(boardTokenParam) : ''),
    [boardTokenParam],
  );

  const [roundMeta, setRoundMeta] = useState<RoundMeta | null>(null);
  const [players, setPlayers] = useState<Record<string, unknown>[]>([]);
  const [scores, setScores] = useState<Record<string, unknown>[]>([]);
  const [totals, setTotals] = useState<Record<string, unknown>[]>([]);
  const [meritsByHole, setMeritsByHole] = useState<MeritsByHole | null>(null);
  const [roundState, setRoundState] = useState<RoundStateEvent | null>(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  // A board opened by code has no user and no claim; the code itself is the whole credential.
  const accessSession = useMemo<AccessCredentials | null>(
    () => (boardToken ? { boardToken } : null),
    [boardToken],
  );
  const headers = useMemo(() => buildAccessHeaders(user?.token), [user?.token]);

  const fetchScoreboard = useCallback(async () => {
    if (!roundId) {
      return;
    }

    try {
      const [roundRes, playersRes, scoreboardRes, meritsRes] = await Promise.all([
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}`, accessSession), { headers }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/players`, accessSession), { headers }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/scoreboard`, accessSession), { headers }),
        // Drives the position graph, and already arrives in exactly the shape the graph wants.
        // Deliberately outside the failure check below: a board without the graph still shows
        // every score, which beats an error screen.
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/merits-by-hole`, accessSession), {
          headers,
        }).catch(() => null),
      ]);

      if (!roundRes.ok || !playersRes.ok || !scoreboardRes.ok) {
        throw new Error('board.loadFailed');
      }

      const [roundData, playersData, scoreboardData] = await Promise.all([
        roundRes.json(),
        playersRes.json(),
        scoreboardRes.json(),
      ]);

      const merits = meritsRes?.ok ? await meritsRes.json().catch(() => null) : null;
      setMeritsByHole(
        Array.isArray(merits?.holes) && Array.isArray(merits?.series)
          ? { holes: merits.holes, series: merits.series }
          : null,
      );

      setRoundMeta(roundData as RoundMeta);
      setPlayers(
        Array.isArray(playersData)
          ? playersData
          : (playersData?.players ?? playersData?.Players ?? []),
      );
      setScores(scoreboardData?.scores ?? scoreboardData?.Scores ?? []);
      setTotals(scoreboardData?.totals ?? scoreboardData?.Totals ?? []);
      setError(null);
      setUpdatedAt(new Date());
    } catch {
      setError(t('roundBoard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [accessSession, apiBaseUrl, headers, roundId, t]);

  useEffect(() => {
    void fetchScoreboard();
  }, [fetchScoreboard]);

  // The socket carries round.state (players, holesCount, and per-hole strokes with their par and
  // stroke index) and hole.update (a score just landed). Points and merits live behind REST, so a
  // score echo means "ask again" — debounced, because a foursome finishing a hole produces four
  // echoes in a second.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!roundId) {
      return;
    }

    const socket = getSharedBffWebSocket(apiBaseUrl);
    if (boardToken && !user?.token) {
      socket.setAnonymousReadOnly(true);
      socket.setAuthToken(null);
      socket.setBoardToken(boardToken);
    }
    socket.setSubscriptionContext({ roundId });

    const scheduleRefresh = () => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
      }
      refreshTimer.current = setTimeout(() => {
        void fetchScoreboard();
      }, REFRESH_DEBOUNCE_MS);
    };

    const unsubscribeMessages = socket.onMessage((message) => {
      const payload = message as { type?: string; roundId?: string };
      if (payload.type === 'round.state' && payload.roundId === roundId) {
        setRoundState(message as RoundStateEvent);
        setUpdatedAt(new Date());
        return;
      }
      if ((payload.type === 'hole.update' || payload.type === 'hole.updated') && payload.roundId === roundId) {
        scheduleRefresh();
      }
    });

    const unsubscribeConnection = socket.onConnectionChange((state) => {
      const isConnected = state === 'connected';
      setConnected(isConnected);
      if (isConnected) {
        // A reconnect means the board may have missed score echoes while the socket was down.
        scheduleRefresh();
      }
    });

    setConnected(socket.isConnected());

    const poll = setInterval(() => {
      void fetchScoreboard();
    }, POLL_INTERVAL_MS);

    return () => {
      unsubscribeMessages();
      unsubscribeConnection();
      clearInterval(poll);
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
      }
    };
  }, [apiBaseUrl, boardToken, fetchScoreboard, roundId, user?.token]);

  // Names come from local translations keyed by the game's GUID — no catalogue call. The API's own
  // `name` field is the English config text, so resolving locally is also what makes a Swedish
  // board say "Slagspel" rather than "Stroke Play".
  const resolveGameName = useCallback(
    (rawGameId: string) => getGameDisplayName({ id: rawGameId }, t('roundBoard.game')),
    [t],
  );

  const standings: BoardStandings = useMemo(
    () =>
      buildBoardStandings({
        players,
        // round.state carries the same players with their names. A second source, so the REST
        // lookup answering [] during a dependency blip cannot blank the board on its own.
        statePlayers: (roundState?.players ?? []) as Record<string, unknown>[],
        scores,
        totals,
        holeEntries: (roundState?.holeEntries ?? []) as Record<string, unknown>[],
        holesCount: roundState?.holesCount ?? null,
        resolveGameName,
      }),
    [players, scores, totals, roundState, resolveGameName],
  );

  const roundName = readText(roundMeta, 'name', 'Name');
  const courseName = readText(roundMeta, 'courseName', 'CourseName');
  const status = readText(roundMeta, 'status', 'Status').toLowerCase();
  const createdAt = readText(roundMeta, 'createdAt', 'CreatedAt');

  /**
   * A round that is over has no hole being played. Saying "hole 14" on a board left up after the
   * prizegiving is the kind of small lie a wall screen tells all evening.
   */
  const roundIsClosed =
    status === 'finished' ||
    status === 'archived' ||
    status === 'cancelled' ||
    status === 'canceled';

  const headline = useMemo(() => {
    const statusLabel = () => {
      if (status === 'finished' || status === 'archived') return t('roundBoard.statusFinished');
      if (status === 'cancelled' || status === 'canceled') return t('roundBoard.statusCancelled');
      if (status === 'draft' || status === 'open') return t('roundBoard.statusNotStarted');
      return t('roundBoard.statusActive');
    };

    let dateLabel = '';
    if (createdAt) {
      const parsed = new Date(createdAt);
      if (!Number.isNaN(parsed.getTime())) {
        dateLabel = parsed.toLocaleDateString(locale || undefined, { day: 'numeric', month: 'short' });
      }
    }

    return [
      courseName || roundName || t('roundBoard.title'),
      // Only when it says something the course name does not — most rounds are named after their
      // course, which is what made the old two-line header repeat itself.
      roundName && roundName !== courseName ? roundName : null,
      dateLabel,
      standings.currentHole && !roundIsClosed
        ? t('roundBoard.currentHole', { hole: standings.currentHole.holeNumber })
        : statusLabel(),
    ]
      .filter(Boolean)
      .join(' · ');
  }, [courseName, createdAt, locale, roundIsClosed, roundName, standings.currentHole, status, t]);

  // How much room the standings actually got. Measured rather than derived from the window: the
  // headline grows with the round's name and the strip below is a fraction of the screen.
  const [tableHeight, setTableHeight] = useState(0);
  const handleTableLayout = useCallback((event: LayoutChangeEvent) => {
    setTableHeight(event.nativeEvent.layout.height);
  }, []);

  /** Enough for four or five names beside the graph, never more than a third of the screen. */
  const stripHeight = Math.max(150, Math.min(320, Math.round(height * 0.28)));

  // One non-player row is always on screen: the column headings.
  const fixedRowCount = 1;
  const rowCount = standings.rows.length;

  /**
   * The measurement is only available after the first paint, and "no measurement yet" must not mean
   * "assume everybody fits" — a full field would paint clipped for a frame before settling, which
   * on a wall screen reads as a glitch. So start from what the window can hold and let onLayout
   * correct it; the estimate is off by the header, not by an order of magnitude.
   */
  const estimatedTableHeight = Math.max(200, height - stripHeight - 140);
  const usableHeight = tableHeight || estimatedTableHeight;

  /**
   * How many players the current-hole panel can name legibly. The panel and the standings show the
   * same page, so whichever holds fewer decides the page — a 30-player field paged by the table
   * alone used to hand the panel thirty names and it overflowed its strip, heading and all.
   */
  const holePanelCapacity = standings.currentHole
    ? Math.max(1, Math.floor((stripHeight - 60) / MIN_HOLE_PANEL_ROW_HEIGHT))
    : Number.POSITIVE_INFINITY;

  const rowsPerPage = useMemo(() => {
    if (rowCount === 0) {
      return 1;
    }
    const usable = usableHeight - fixedRowCount * MIN_ROW_HEIGHT;
    const tableCapacity = Math.floor(usable / MIN_ROW_HEIGHT);
    return Math.max(1, Math.min(rowCount, tableCapacity, holePanelCapacity));
  }, [fixedRowCount, holePanelCapacity, rowCount, usableHeight]);

  const pageCount = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const [page, setPage] = useState(0);

  // A field too long for one screen turns instead of scrolling. Nothing to do this for when it fits.
  useEffect(() => {
    if (pageCount <= 1) {
      setPage(0);
      return;
    }
    const timer = setInterval(() => setPage((current) => (current + 1) % pageCount), PAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pageCount]);

  // A page that vanished under the board's feet — players left, or the screen was resized — would
  // otherwise leave it showing an empty page until the next tick.
  const safePage = page < pageCount ? page : 0;
  const visibleRows = useMemo(
    () => standings.rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [rowsPerPage, safePage, standings.rows],
  );

  /**
   * Fill the height that is there, sized off the page's CAPACITY rather than how many rows this
   * particular page happens to hold. A final page carrying four stragglers must not blow them up to
   * five times the height of the twenty-six on the page before.
   */
  const rowHeight = useMemo(() => {
    const perRow = usableHeight / (rowsPerPage + fixedRowCount);
    return Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, perRow));
  }, [fixedRowCount, rowsPerPage, usableHeight]);

  // Every font is a fraction of the row it sits in, so the whole board scales together rather than
  // needing a breakpoint per screen size.
  const bodyFont = Math.round(Math.max(13, Math.min(30, rowHeight * 0.42)));
  const headFont = Math.round(Math.max(10, Math.min(16, rowHeight * 0.24)));
  const nameFont = Math.round(bodyFont * 1.05);
  const avatarSize = Math.round(bodyFont * 1.5);
  const positionColumnWidth = Math.round(bodyFont * 2.2);
  const numberColumnWidth = Math.round(bodyFont * 2.9);
  const gameColumnWidth = Math.round(bodyFont * 4.6);
  const totalColumnWidth = Math.round(bodyFont * 3.2);

  // The graph keys its names by whatever id its own series carry, so build the lookup from those
  // rather than from our case-folded row ids.
  const graphNameById = useMemo(() => {
    const byLowerId = new Map(standings.rows.map((row) => [row.playerId, row.name]));
    const lookup = new Map<string, string>();
    for (const entry of meritsByHole?.series ?? []) {
      const name = byLowerId.get(String(entry.playerId).toLowerCase());
      if (name) {
        lookup.set(entry.playerId, name);
      }
    }
    return lookup;
  }, [meritsByHole, standings.rows]);

  const showGraph = Boolean(meritsByHole?.holes.length && meritsByHole.series.length);

  if (!roundId) {
    return (
      <View style={styles.centered}>
        <Text style={styles.error}>{t('roundBoard.missingRound')}</Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={appColors.accent.primary} />
        <Text style={styles.loadingText}>{t('roundBoard.loading')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <RNView style={styles.header}>
        <Text testID="board-headline" style={styles.headline} numberOfLines={1}>
          {headline}
        </Text>
        <RNView style={styles.headerMeta}>
          <RNView style={styles.liveDot} />
          <Text style={styles.liveText}>
            {connected ? t('roundBoard.live') : t('roundBoard.reconnecting')}
          </Text>
          {updatedAt ? (
            <Text style={styles.updatedText}>
              {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          ) : null}
          {pageCount > 1 ? (
            <Text testID="board-page" style={styles.updatedText}>
              {t('roundBoard.page', { page: safePage + 1, pages: pageCount })}
            </Text>
          ) : null}
          {boardToken ? (
            <Text testID="board-code" style={styles.codeValue}>
              {formatBoardCode(boardToken)}
            </Text>
          ) : null}
        </RNView>
      </RNView>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {standings.rows.length === 0 ? (
        <RNView style={styles.emptyWrap}>
          <Text style={styles.empty}>{t('roundBoard.noPlayers')}</Text>
        </RNView>
      ) : (
        <>
          <RNView style={styles.table} onLayout={handleTableLayout}>
            <RNView style={[styles.row, styles.headRow, { height: rowHeight }]}>
              <Text style={[styles.cell, styles.headCell, { width: positionColumnWidth, fontSize: headFont }]}>
                {t('roundBoard.pos')}
              </Text>
              <RNView style={{ width: avatarSize }} />
              <Text style={[styles.cell, styles.nameCell, styles.headCell, { fontSize: headFont }]}>
                {t('roundBoard.player')}
              </Text>
              <Text style={[styles.cell, styles.headCell, { width: numberColumnWidth, fontSize: headFont }]}>
                {t('roundBoard.thru')}
              </Text>
              <Text style={[styles.cell, styles.headCell, { width: numberColumnWidth, fontSize: headFont }]}>
                {t('roundBoard.gross')}
              </Text>
              <Text style={[styles.cell, styles.headCell, { width: numberColumnWidth, fontSize: headFont }]}>
                {t('roundBoard.net')}
              </Text>
              {standings.games.map((game) => (
                <Text
                  key={game.id}
                  testID={`board-game-head-${game.id}`}
                  style={[styles.cell, styles.headCell, { width: gameColumnWidth, fontSize: headFont }]}
                  numberOfLines={1}
                >
                  {game.name || t('roundBoard.game')}
                </Text>
              ))}
              <Text
                style={[
                  styles.cell,
                  styles.headCell,
                  styles.totalCell,
                  { width: totalColumnWidth, fontSize: headFont },
                ]}
              >
                {t('roundBoard.merits')}
              </Text>
            </RNView>

            {visibleRows.map((row, index) => (
              <RNView
                key={row.playerId}
                testID={`board-row-${row.playerId}`}
                style={[
                  styles.row,
                  { height: rowHeight },
                  index % 2 === 1 && styles.rowAlt,
                  row.position === 1 && styles.rowLeader,
                ]}
              >
                <Text
                  testID={`board-position-${row.playerId}`}
                  style={[styles.cell, styles.posText, { width: positionColumnWidth, fontSize: bodyFont }]}
                >
                  {row.position}
                </Text>
                <PlayerAvatar
                  size={avatarSize}
                  name={row.name}
                  isAi={row.isAi}
                  source={getPlayerAvatarImageSource(apiBaseUrl, {
                    id: row.playerId,
                    imageKey: row.imageKey,
                    imageThumbKey: row.imageThumbKey,
                  })}
                />
                <Text
                  style={[styles.cell, styles.nameCell, styles.nameText, { fontSize: nameFont }]}
                  numberOfLines={1}
                >
                  {row.name}
                </Text>
                <Text
                  testID={`board-thru-${row.playerId}`}
                  style={[styles.cell, { width: numberColumnWidth, fontSize: bodyFont }]}
                >
                  {row.thru || '–'}
                </Text>
                <Text style={[styles.cell, { width: numberColumnWidth, fontSize: bodyFont }]}>
                  {row.grossStrokes ?? '–'}
                </Text>
                <Text
                  testID={`board-net-${row.playerId}`}
                  style={[styles.cell, { width: numberColumnWidth, fontSize: bodyFont }]}
                >
                  {row.netStrokes ?? '–'}
                </Text>
                {standings.games.map((game) => {
                  const cell = row.cellsByGameId[game.id];
                  return (
                    <Text
                      key={game.id}
                      testID={`board-game-${game.id}-${row.playerId}`}
                      style={[styles.cell, { width: gameColumnWidth, fontSize: bodyFont }]}
                    >
                      <Text
                        testID={`board-game-points-${game.id}-${row.playerId}`}
                        style={styles.scorePoints}
                      >
                        {formatNumber(cell?.scorePoints ?? null)}p{' '}
                      </Text>
                      <Text
                        testID={`board-game-merits-${game.id}-${row.playerId}`}
                        style={
                          (cell?.merits ?? 0) < 0 ? styles.meritsNegative : styles.meritsPositive
                        }
                      >
                        {formatNumber(cell?.merits ?? null)}
                      </Text>
                    </Text>
                  );
                })}
                <Text
                  testID={`board-total-${row.playerId}`}
                  style={[
                    styles.cell,
                    styles.totalCell,
                    styles.totalText,
                    { width: totalColumnWidth, fontSize: bodyFont },
                  ]}
                >
                  {formatNumber(row.totalMerits)}
                </Text>
              </RNView>
            ))}
          </RNView>

          <RNView style={[styles.strip, { height: stripHeight }]}>
            {standings.currentHole ? (
              <RNView style={styles.stripHole}>
                <BoardCurrentHole
                  hole={standings.currentHole}
                  rows={visibleRows}
                  labelFont={headFont}
                  closed={roundIsClosed}
                />
              </RNView>
            ) : null}
            {showGraph && meritsByHole ? (
              <RNView style={styles.stripGraph} testID="board-position-graph">
                <Text style={[styles.stripLabel, { fontSize: headFont }]}>
                  {t('roundBoard.positionByHole')}
                </Text>
                <PositionByHoleGraph
                  holes={meritsByHole.holes}
                  series={meritsByHole.series}
                  playerNameById={graphNameById}
                />
              </RNView>
            ) : null}
          </RNView>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // The whole board is one screen. flex:1 with no scroll view anywhere is what makes that true —
  // a wall display has nobody to scroll it, so anything below the fold may as well not exist.
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 14,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: appColors.background.app,
  },
  loadingText: {
    color: appColors.text.secondary,
    fontSize: 22,
  },
  // One line. The old two-line block spent a tenth of a wall screen repeating the course name.
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    paddingBottom: 10,
  },
  headline: {
    color: appColors.text.primary,
    fontSize: 28,
    fontWeight: '800',
    flexShrink: 1,
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  liveDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: appColors.accent.primary,
  },
  liveText: {
    color: appColors.text.secondary,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    fontWeight: '700',
  },
  updatedText: {
    color: appColors.text.muted,
    fontSize: 13,
  },
  codeValue: {
    color: appColors.accent.light,
    fontSize: 24,
    letterSpacing: 3,
    fontWeight: '800',
  },
  table: {
    flex: 1,
    justifyContent: 'center',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  headRow: {
    backgroundColor: appColors.background.panel,
  },
  rowAlt: {
    backgroundColor: alpha(appColors.surface.tintDeep, 0.18),
  },
  rowLeader: {
    borderLeftWidth: 4,
    borderLeftColor: appColors.accent.primary,
  },
  cell: {
    paddingHorizontal: 6,
    color: appColors.text.primary,
    textAlign: 'center',
  },
  headCell: {
    fontWeight: '700',
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  // The only flexing column, so the table always fills the width however many games there are.
  nameCell: {
    flex: 1,
    textAlign: 'left',
    paddingLeft: 10,
  },
  posText: {
    fontWeight: '800',
    color: appColors.accent.light,
  },
  nameText: {
    fontWeight: '700',
  },
  scorePoints: {
    color: appColors.text.muted,
  },
  meritsPositive: {
    color: appColors.accent.light,
    fontWeight: '700',
  },
  meritsNegative: {
    color: appColors.status.errorText,
    fontWeight: '700',
  },
  totalCell: {
    backgroundColor: alpha(appColors.accent.primary, 0.12),
  },
  totalText: {
    fontWeight: '800',
  },
  strip: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
  },
  stripHole: {
    flex: 1,
  },
  stripGraph: {
    flex: 3,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  stripLabel: {
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '700',
    marginBottom: 4,
  },
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  empty: {
    color: appColors.text.secondary,
    fontSize: 24,
    textAlign: 'center',
  },
  error: {
    color: appColors.status.errorText,
    fontSize: 18,
    paddingBottom: 8,
  },
});
